import { trackAcceptedLead } from './analytics';
import { clearSubmissionReference, sendLead, submissionReference } from './leadClient';
import { clearFieldError, validateLeadForm } from './leadValidation';

const formSources = { 'jil-id-form-one-195': 'consultation', 'jil-id-form-two-206': 'contact' };

export function initializeLeadForms(root, on, onAccepted) {
  let disposed = false;
  const activeForms = new Set();
  const owned = form => form && root.contains(form) && Object.prototype.hasOwnProperty.call(formSources, form.id);
  const prepare = form => {
    form.noValidate = true;
    let error = form.querySelector('.jos-form-error');
    if (!error) {
      error = document.createElement('p'); error.className = 'jos-form-error'; error.hidden = true;
      error.dataset.testid = `${formSources[form.id]}-form-error`; error.setAttribute('role', 'alert'); error.tabIndex = -1;
      form.prepend(error);
    }
    if (!form.querySelector('[name="website"]')) {
      const trap = document.createElement('input'); trap.name = 'website'; trap.type = 'text';
      trap.tabIndex = -1; trap.autocomplete = 'off'; trap.className = 'jos-form-honeypot'; trap.setAttribute('aria-hidden', 'true');
      trap.dataset.testid = `${formSources[form.id]}-form-website`; form.appendChild(trap);
    }
    return error;
  };
  root.querySelectorAll('.jil-lead-form-104').forEach(form => {
    prepare(form);
    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = false;
  });

  const submit = async form => {
    if (disposed || activeForms.has(form.id)) return;
    const error = prepare(form);
    const showError = (message, focus = true) => {
      error.textContent = message; error.hidden = false;
      if (focus) {
        try { error.focus({ preventScroll: true }); } catch { error.focus(); }
        error.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };
    if (!validateLeadForm(form, showError)) return;
    const button = form.querySelector('button[type="submit"]');
    if (!button) { showError('The form is unavailable. Please call 949-883-6002.'); return; }
    const fields = Array.from(form.querySelectorAll('input, select, textarea'));
    const originalDisabled = fields.map(field => field.disabled);
    const originalButton = button.innerHTML;
    activeForms.add(form.id); error.hidden = true;
    try {
      const data = new FormData(form);
      const payload = {};
      ['first_name', 'last_name', 'phone', 'email', 'case_type', 'preferred_language', 'message', 'website'].forEach(name => {
        payload[name] = String(data.get(name) || '').trim();
      });
      payload.consent = data.get('consent') === 'on'; payload.source = formSources[form.id];
      payload.submission_id = submissionReference(form.id, payload);
      button.disabled = true; button.textContent = 'Sending…'; button.setAttribute('aria-busy', 'true');
      fields.forEach(field => { field.disabled = true; });
      let response;
      for (let attempt = 0; attempt < 20 && !disposed; attempt++) {
        response = await sendLead(payload);
        if (disposed) return;
        if (response.status !== 202 || response.result?.id !== payload.submission_id) throw new Error('We could not confirm your request. Please try again.');
        if (response.result.status === 'accepted') break;
        if (response.result.status !== 'processing') throw new Error('We could not confirm your request. Please try again.');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      if (disposed) return;
      if (response?.result.status !== 'accepted') throw new Error('Your request is still processing. Your details are saved in this form. Please try again shortly.');
      trackAcceptedLead({ receipt: response.result, httpStatus: response.status, submissionId: payload.submission_id, source: payload.source });
      clearSubmissionReference(form.id);
      form.reset();
      form.querySelectorAll('select').forEach(select => select.classList.add('jil-placeholder-108'));
      onAccepted();
    } catch (failure) {
      if (!disposed) showError(failure instanceof TypeError ? 'We could not send your request. Please try again or call 949-883-6002.' : failure.message);
    } finally {
      activeForms.delete(form.id);
      button.disabled = false; button.innerHTML = originalButton; button.removeAttribute('aria-busy');
      fields.forEach((field, index) => { field.disabled = originalDisabled[index]; });
    }
  };

  // Capture on the stable root, not a replaceable form/button. Do not stop propagation:
  // analytics can still observe clicks, but cannot become the submission mechanism.
  on(root, 'click', event => {
    const button = event.target.closest?.('button[type="submit"], input[type="submit"]');
    if (!owned(button?.form)) return;
    event.preventDefault(); void submit(button.form);
  }, true);
  on(root, 'submit', event => {
    if (!owned(event.target)) return;
    event.preventDefault(); void submit(event.target);
  }, true);
  on(root, 'input', event => { if (owned(event.target.form)) clearFieldError(event.target); });
  on(root, 'change', event => {
    if (!owned(event.target.form)) return;
    clearFieldError(event.target);
    if (event.target.tagName === 'SELECT') event.target.classList.toggle('jil-placeholder-108', !event.target.value);
  });
  return () => {
    disposed = true;
    root.querySelectorAll('[data-jos-error-id]').forEach(clearFieldError);
    root.querySelectorAll('.jos-form-error, .jos-form-honeypot').forEach(el => el.remove());
  };
}